/** Optional merchant brief; saves only answers the merchant changes. */
import { useState } from 'react'
import {
  merchantOnboardingAnswerSchemas,
  merchantOnboardingRegistry,
  type MerchantOnboardingAnswerInput,
} from '@testfeed/shopify-contracts'
import type { OnboardingField } from './onboardingDetailsApi'

type BriefKey = 'business_description' | 'primary_goal'

function initialAnswer(fields: OnboardingField[], key: BriefKey): string {
  const answer = fields.find((field) => field.fieldKey === key)?.answer
  return typeof answer === 'string' ? answer : ''
}

/** Existing answers for other fields remain untouched by this partial finish. */
export function OnboardingDetailsForm({
  fields,
  saving,
  error,
  onSave,
}: {
  fields: OnboardingField[]
  saving: boolean
  error: string | null
  onSave: (answers: MerchantOnboardingAnswerInput[]) => Promise<boolean>
}) {
  const [description, setDescription] = useState(() => initialAnswer(fields, 'business_description'))
  const [goal, setGoal] = useState(() => initialAnswer(fields, 'primary_goal'))
  const [changed, setChanged] = useState<Set<BriefKey>>(() => new Set())
  const [validation, setValidation] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const markChanged = (key: BriefKey) => {
    setChanged((current) => new Set(current).add(key))
    setValidation(null)
    setSaved(false)
  }
  const submit = async () => {
    const values = { business_description: description, primary_goal: goal }
    const answers: MerchantOnboardingAnswerInput[] = []
    for (const fieldKey of changed) {
      const value = values[fieldKey].trim()
      // The existing API upserts answers; it does not delete string answers.
      if (!value) {
        if (fields.some((field) => field.fieldKey === fieldKey)) {
          setValidation('Saved answers cannot be removed here. Enter a replacement, or close without saving.')
          return
        }
        continue
      }
      if (value === initialAnswer(fields, fieldKey).trim()) continue
      const parsed = merchantOnboardingAnswerSchemas[fieldKey].safeParse(value)
      if (!parsed.success) {
        setValidation(
          fieldKey === 'business_description'
            ? 'Enter at least 10 characters for your business description.'
            : 'Choose an outcome from the list.',
        )
        return
      }
      answers.push({ fieldKey, answer: parsed.data, origin: 'merchant_entered' })
    }
    if (answers.length === 0) {
      setValidation('There are no changes to save.')
      return
    }
    if (await onSave(answers)) {
      setChanged(new Set())
      setSaved(true)
    }
  }
  return (
    <s-stack gap="base">
      <s-paragraph>
        Share a little about your store to help us understand what you want to learn. Both answers are optional.
      </s-paragraph>
      {error && <s-banner tone="critical">{error}</s-banner>}
      {validation && <s-banner tone="warning">{validation}</s-banner>}
      {saved && <s-banner tone="success">Your details are saved.</s-banner>}
      <s-text-area
        disabled={saving}
        label={merchantOnboardingRegistry.fields.business_description.question}
        value={description}
        rows={3}
        onInput={(event) => {
          setDescription(event.currentTarget.value)
          markChanged('business_description')
        }}
      />
      <s-select
        disabled={saving}
        label={merchantOnboardingRegistry.fields.primary_goal.question}
        value={goal}
        onChange={(event) => {
          setGoal(event.currentTarget.value)
          markChanged('primary_goal')
        }}
      >
        <s-option value="">Choose an outcome</s-option>
        {(merchantOnboardingRegistry.fields.primary_goal.options ?? []).map((option) => (
          <s-option key={option.id} value={option.id}>
            {option.label}
          </s-option>
        ))}
      </s-select>
      <s-stack direction="inline" gap="small">
        <s-button variant="primary" loading={saving} disabled={saving} onClick={() => void submit()}>
          Save details
        </s-button>
      </s-stack>
    </s-stack>
  )
}
